import React, { useState } from "react";
import { Search, Bell, Plus, ChevronDown, LogOut, User as UserIcon } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export default function Header({ breadcrumbs = [] }) {
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const initials = (profile?.full_name || "")
    .split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase() || "?";

  const logout = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <header data-testid="app-header" className="h-16 sticky top-0 z-20 bg-[hsl(var(--bg-base))]/80 backdrop-blur border-b border-[hsl(var(--border-divider))]">
      <div className="h-full px-6 md:px-8 flex items-center gap-4">
        <nav className="hidden md:flex items-center gap-2 text-sm" aria-label="Breadcrumb">
          {breadcrumbs.length === 0 ? (
            <span className="text-[hsl(var(--text-tertiary))]">Visão geral</span>
          ) : breadcrumbs.map((b, i) => (
            <React.Fragment key={i}>
              {i > 0 && <span className="text-[hsl(var(--text-tertiary))]">/</span>}
              <span className={i === breadcrumbs.length - 1 ? "text-[hsl(var(--text-primary))] font-medium" : "text-[hsl(var(--text-tertiary))]"}>{b}</span>
            </React.Fragment>
          ))}
        </nav>

        <div className="flex-1 max-w-xl ml-auto md:ml-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-tertiary))]" />
            <input
              data-testid="global-search-input"
              type="text"
              placeholder="Buscar paciente..."
              className="ds-input pl-9 h-10"
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.currentTarget.value.trim()) {
                  navigate(`/app/pacientes?q=${encodeURIComponent(e.currentTarget.value.trim())}`);
                }
              }}
            />
          </div>
        </div>

        <button data-testid="header-quick-invite" onClick={() => navigate("/app/convites")} className="ds-btn-primary hidden sm:inline-flex">
          <Plus className="w-4 h-4" /> Convidar paciente
        </button>

        <button data-testid="header-notifications" className="relative w-10 h-10 rounded-lg hover:bg-[hsl(var(--bg-hover))] flex items-center justify-center text-[hsl(var(--text-secondary))]" aria-label="Notificações">
          <Bell className="w-[18px] h-[18px]" />
        </button>

        <div className="relative">
          <button data-testid="header-user-menu" onClick={() => setMenuOpen(o => !o)} className="flex items-center gap-2.5 pl-1 pr-2.5 h-10 rounded-lg hover:bg-[hsl(var(--bg-hover))] transition-colors">
            <div className="w-8 h-8 rounded-full bg-[hsl(var(--brand))] text-white text-xs font-semibold flex items-center justify-center">{initials}</div>
            <div className="hidden md:block text-left leading-tight">
              <div className="text-sm font-medium">{profile?.full_name || "—"}</div>
              <div className="text-[11px] text-[hsl(var(--text-tertiary))]">{profile?.email}</div>
            </div>
            <ChevronDown className="hidden md:block w-4 h-4 text-[hsl(var(--text-tertiary))]" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-12 w-56 ds-card p-1 shadow-xl z-30">
              <button onClick={() => { setMenuOpen(false); navigate("/app/configuracoes"); }} className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md hover:bg-[hsl(var(--bg-hover))]">
                <UserIcon className="w-4 h-4" /> Perfil & conta
              </button>
              <button data-testid="header-logout" onClick={logout} className="flex items-center gap-2 w-full px-3 py-2 text-sm rounded-md hover:bg-[hsl(var(--bg-hover))] text-[hsl(var(--danger-text))]">
                <LogOut className="w-4 h-4" /> Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
